import React, { useState } from 'react';
import { PenLine, Check, X } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { CumChart, CLASS_COLORS } from './CumChart';
import { SlaBars } from './SlaBars';

const SplitLine = ({ split, classTargets, testId }) => !split ? null : (
  <p className="text-[10px] font-mono mt-0.5" data-testid={testId}>
    {['A', 'B', 'C'].map((c, i) => (
      <span key={c}>{i > 0 && <span className="text-muted-foreground"> · </span>}
        <span style={{ color: CLASS_COLORS[c] }}>{c} {split[c] ?? 0}{classTargets?.[c] != null ? <span className="text-muted-foreground">/{classTargets[c]}</span> : null}</span>
      </span>
    ))}
    {split.none > 0 && <span className="text-muted-foreground"> · ohne Klasse {split.none}</span>}
  </p>
);

const MainNumber = ({ card }) => {
  const k = card.kind;
  if (k === 'stock' && card.key === 'team_buffer') {
    return <><span className="text-3xl font-heading text-foreground">{card.weeks}</span><span className="text-sm text-muted-foreground ml-1">Wochen</span><span className="text-xs text-muted-foreground ml-2">({card.total} Briefe)</span></>;
  }
  if (k === 'stock') {
    return <><span className="text-3xl font-heading text-foreground">{card.total}</span>{card.target ? <span className="text-sm text-muted-foreground ml-1">/ ~{card.target}</span> : null}</>;
  }
  if (k === 'hours') {
    return <><span className="text-3xl font-heading text-foreground">{card.total ?? '—'}</span><span className="text-sm text-muted-foreground ml-1">h Median</span>{card.target ? <span className="text-xs text-muted-foreground ml-2">Ziel ≤ {card.target} h</span> : null}</>;
  }
  if (k === 'pct') {
    return <><span className="text-3xl font-heading text-foreground">{card.total} %</span>{card.target ? <span className="text-sm text-muted-foreground ml-1">/ {card.target} %</span> : null}</>;
  }
  if (k === 'ratio') {
    return <><span className="text-3xl font-heading text-foreground">{card.total}</span><span className="text-sm text-muted-foreground ml-1">von {card.target} fällig</span><span className="text-xs text-muted-foreground ml-2">({card.pct} %)</span></>;
  }
  return <><span className="text-3xl font-heading text-foreground">{card.total}</span>{card.target ? <span className="text-sm text-muted-foreground ml-1">/ {card.target}</span> : null}</>;
};

// Talu, 03./04.11.2026: one click shows every open teaser — overdue first (red), then the ones still inside the SLA.
const OpenTeasers = ({ card }) => {
  const [open, setOpen] = React.useState(false);
  const items = card.open_items || [];
  const overdue = items.filter((it) => it.overdue).length;
  const label = items.length
    ? `${items.length} Teaser offen${overdue ? ` · ${overdue} überfällig` : ''}${open ? ' ▴' : ' ▾'}`
    : 'kein Teaser offen';
  return (
    <div className="mt-1.5" data-testid="kpi-open-teasers">
      <button onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} data-testid="kpi-open-teasers-toggle"
        className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${overdue ? 'border-red-500/50 text-red-300 hover:bg-red-500/10' : items.length ? 'border-amber-500/50 text-amber-300 hover:bg-amber-500/10' : 'border-border text-muted-foreground'}`}>
        {label}
      </button>
      {open && items.length > 0 && (
        <ul className="mt-1.5 space-y-1" data-testid="kpi-open-teasers-list">
          {items.map((it) => (
            <li key={it.id} className="text-[11px] flex items-center justify-between gap-2" data-testid={`kpi-open-teaser-${it.id}`}>
              <a href={it.project_id ? `/projects/${it.project_id}` : '/deal-inbox'} className="text-foreground hover:underline truncate" onClick={(e) => e.stopPropagation()}>
                {it.name}{it.broker ? <span className="text-muted-foreground"> · {it.broker}</span> : null}
              </a>
              <span className={`font-mono shrink-0 ${it.overdue ? 'text-red-300' : 'text-muted-foreground'}`}>
                {it.overdue ? `${it.days_overdue} d überfällig` : `seit ${it.days_since_receipt} d · fällig ${new Date(it.due_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const WindowTargetEditor = ({ card, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(card.target ?? '');
  if (!editing) {
    return <button onClick={() => { setVal(card.target ?? ''); setEditing(true); }} data-testid={`kpi-window-target-edit-${card.key}`}
      className="text-[9px] text-muted-foreground hover:text-sky-300 inline-flex items-center gap-0.5" title="Ziel nur für dieses Fenster ändern"><PenLine className="w-3 h-3" /> Ziel</button>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`kpi-window-target-input-${card.key}`}
        className="w-14 h-5 text-[10px] bg-secondary border border-border rounded-sm px-1 text-foreground" />
      <button onClick={async () => { await onSave(card, val); setEditing(false); }} data-testid={`kpi-window-target-save-${card.key}`} className="text-emerald-300"><Check className="w-3 h-3" /></button>
      <button onClick={() => setEditing(false)} className="text-muted-foreground"><X className="w-3 h-3" /></button>
    </span>
  );
};

export const KpiCard = ({ card, offset, isAdmin, onSetWindowTarget }) => {
  const win = card.window;
  const allowSplit = card.section !== 'talu';
  return (
    <div className={`border bg-card rounded-sm p-3 sm:p-4 min-w-0 ${card.primary ? 'border-sky-500/40' : 'border-border'}`} data-testid={`kpi-card-${card.key}`}>
      <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2 mb-1">
        <div className="min-w-0">
          <h3 className="text-xs sm:text-sm font-heading text-foreground sm:truncate">{card.label}{card.primary && <span className="ml-1 text-[9px] font-mono text-sky-300 uppercase">Primär</span>}</h3>
          {win ? (
            <p className="text-[9px] font-mono text-muted-foreground" data-testid={`kpi-window-${card.key}`}>
              Deadline {win.deadline_label} · Fenster {win.label}{win.days > 7 ? ` (${win.days} Tage)` : ''}
            </p>
          ) : <p className="text-[9px] font-mono text-muted-foreground">laufend</p>}
        </div>
        <StatusBadge status={card.status} label={card.status_label} testId={`kpi-status-${card.key}`} />
      </div>
      <div className="flex items-baseline gap-1 flex-wrap" data-testid={`kpi-total-${card.key}`}>
        <MainNumber card={card} />
        {isAdmin && card.weekly_editable && onSetWindowTarget && <span className="ml-auto"><WindowTargetEditor card={card} onSave={onSetWindowTarget} /></span>}
      </div>
      {card.has_split && card.kind !== 'ratio' && <SplitLine split={card.split} classTargets={card.class_targets} testId={`kpi-split-${card.key}`} />}
      {card.subline && (card.kind === 'ratio' || !card.has_split || card.section === 'talu' || card.kind === 'stock') && (
        <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`kpi-subline-${card.key}`}>{card.subline}</p>
      )}
      {card.key === 'talu_expose_sla' && <OpenTeasers card={card} />}
      {card.rework_count > 0 && <p className="text-[10px] text-amber-300 mt-0.5" data-testid={`kpi-rework-${card.key}`}>davon {card.rework_count} nach der Deadline nachgearbeitet</p>}
      {card.by_author?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid="kpi-letters-by-author">
          {card.by_author.map((a) => <span key={a.name} className="text-[10px] font-mono border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground">{a.name} <span className="text-foreground">{a.count}</span></span>)}
        </div>
      )}
      {card.chart === 'cum' && <div className="mt-2"><CumChart card={card} allowSplit={allowSplit} /></div>}
      {card.chart === 'bars' && <div className="mt-2"><SlaBars card={card} /></div>}
    </div>
  );
};
